/**
 * RealEstateHome — /realestate
 * Public hub — hero, search, 4 category cards, latest listings.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PROP_CATEGORIES, propLabel, propGroupFor } from "@/data/propertyCategories";
import { Search, MapPin, Home, Plus, Loader2, Wifi, Bed, Bath, Ruler, Heart } from "lucide-react";

function fmtPrice(price, currency = "USD", period) {
  if (price == null) return "Contact for price";
  try {
    const s = new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(price);
    return period ? `${s}/${period.replace("per-","")}` : s;
  } catch { return `${currency} ${price}`; }
}

function PropertyCard({ p }) {
  const g = propGroupFor(p.category);
  return (
    <Link to={`/realestate/${p.slug || p.id}`} className="wofy-card !p-0 overflow-hidden hover:shadow-md transition-shadow" data-testid={`property-card-${p.slug || p.id}`}>
      <div className="aspect-[4/3] bg-black/5 overflow-hidden relative">
        {p.photos?.[0]
          ? <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full grid place-items-center text-5xl opacity-30">{g?.emoji || "🏡"}</div>}
        <span className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: p.sale_or_rent === "sale" ? "#0E7C86" : "#B25D38" }}>
          For {p.sale_or_rent}
        </span>
      </div>
      <div className="p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{propLabel(p.category)}</div>
        <div className="font-display text-lg leading-tight mt-0.5">{p.title}</div>
        <div className="text-xs opacity-80 mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[p.city, p.country].filter(Boolean).join(", ") || "—"}</div>
        <div className="mt-2 flex items-center justify-between">
          <div className="font-semibold" style={{ color: "var(--wofy-primary)" }}>{fmtPrice(p.price, p.currency, p.price_period)}</div>
          <div className="text-[11px] opacity-70 flex items-center gap-2">
            {p.features?.bedrooms != null && <span className="inline-flex items-center gap-1"><Bed className="w-3 h-3" /> {p.features.bedrooms}</span>}
            {p.features?.bathrooms != null && <span className="inline-flex items-center gap-1"><Bath className="w-3 h-3" /> {p.features.bathrooms}</span>}
            {p.size_value && <span className="inline-flex items-center gap-1"><Ruler className="w-3 h-3" /> {p.size_value} {p.size_unit}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function RealEstateHome() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [sor, setSor] = useState(params.get("sale_or_rent") || "");
  const [bedrooms, setBedrooms] = useState(params.get("bedrooms") || "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = {};
      if (q) query.q = q; if (category) query.category = category;
      if (sor) query.sale_or_rent = sor; if (bedrooms) query.bedrooms = Number(bedrooms);
      const { data } = await http.get("/properties", { params: query });
      setItems(data.items || []); setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [q, category, sor, bedrooms]);
  useEffect(() => { load(); }, [load]);

  const runSearch = () => {
    const p = {};
    if (q) p.q = q; if (category) p.category = category;
    if (sor) p.sale_or_rent = sor; if (bedrooms) p.bedrooms = bedrooms;
    setParams(p);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="realestate-home">
      <section className="rounded-xl p-6 md:p-10 mb-8" style={{ background: "var(--wofy-surface)" }}>
        <div className="overline mb-2">SOCIAL PULSE REAL ESTATE</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">Buy. Sell. Rent. Build. Improve.</h1>
        <p className="max-w-2xl opacity-80">
          List your home, apartment, land or vacation rental — for free. Discover properties, connect
          with agents, builders and neighbours.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2 wofy-card !p-3">
          <div className="flex items-center gap-1.5 pl-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 opacity-60" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="'3-bedroom in Kochi' or 'villa Goa'" className="text-sm bg-transparent outline-none flex-1" data-testid="re-search-input" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-sm bg-transparent outline-none" data-testid="re-category">
            <option value="">All types</option>
            {Object.entries(PROP_CATEGORIES).flatMap(([gid, g]) =>
              g.subs.map((s) => <option key={`${gid}-${s}`} value={s}>{propLabel(s)} ({g.label})</option>))}
          </select>
          <select value={sor} onChange={(e) => setSor(e.target.value)} className="text-sm bg-transparent outline-none" data-testid="re-sor">
            <option value="">Sale or rent</option>
            <option value="sale">For sale</option>
            <option value="rent">For rent</option>
          </select>
          <select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} className="text-sm bg-transparent outline-none" data-testid="re-bedrooms">
            <option value="">Any bedrooms</option>
            {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}+ bed</option>)}
          </select>
          <button onClick={runSearch} className="wofy-btn-primary" data-testid="re-search-btn">Search</button>
          {user && (
            <Link to="/realestate/post" className="wofy-btn-ghost inline-flex items-center gap-1.5" data-testid="re-post-cta">
              <Plus className="w-4 h-4" /> List a property
            </Link>
          )}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="font-display text-2xl mb-3">Browse by type</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(PROP_CATEGORIES).map(([gid, g]) => (
            <Link key={gid} to={`/realestate?category=${g.subs[0]}`} className="wofy-card !p-4 flex items-start gap-3 hover:shadow-md transition-shadow" data-testid={`re-cat-${gid}`}>
              <span className="text-3xl">{g.emoji}</span>
              <div>
                <div className="font-display text-lg leading-tight">{g.label}</div>
                <div className="text-[11px] opacity-70 mt-0.5">{g.subs.length} sub-types</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl">{q || category || sor || bedrooms ? "Matching listings" : "Latest listings"}</h2>
          <span className="text-xs opacity-70">{total} listing{total === 1 ? "" : "s"}</span>
        </div>
        {loading ? <div className="py-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>
         : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center" data-testid="re-empty">
            <Home className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <p className="font-display text-xl mb-1">Be the first to list</p>
            <p className="text-sm opacity-70 mb-4">No properties match — or none listed yet.</p>
            {user && <Link to="/realestate/post" className="wofy-btn-primary inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> List a property</Link>}
          </div>
         ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="re-results">
            {items.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
